import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { Send, Zap, Paperclip, Mic, ArrowLeft, AlertTriangle, TrendingUp, Plus, Clock, User, Target } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { DEALS_DATA, DEALS_CHAT_RESPONSES } from '../../data/dealsData';
import V4AdHocChat from './V4AdHocChat';
import { buildDealDeepDive } from '../../data/davidDealsDeepDiveData';
import { DAVID_DEALS_CREATE, DAVID_DEALS_KEYPRESS, DAVID_INSIGHT_DD } from '../../data/davidDealsCreateKeypressData';

const STAGE_COLORS = {
  'Prospecting':  { bg: 'bg-slate-500/10', text: 'text-slate-500', border: 'border-slate-500/20' },
  'Qualified':    { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  'Proposal':     { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
  'Negotiation':  { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  'Closed Won':   { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  'Closed Lost':  { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
};

const LIGHT_STAGE_COLORS = {
  'Prospecting':  { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  'Qualified':    { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  'Proposal':     { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  'Negotiation':  { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  'Closed Won':   { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  'Closed Lost':  { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

const DealCard = ({ deal, isDark, onDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const isNew = deal.isNew;
  const cardBg = isNew
    ? (isDark ? 'bg-indigo-950/20' : 'bg-indigo-50/60')
    : deal.health === 'at-risk'
      ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40')
      : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = isNew
    ? (isDark ? 'border-indigo-500/25' : 'border-indigo-300/60')
    : deal.health === 'at-risk'
      ? (isDark ? 'border-red-500/15' : 'border-red-200/50')
      : border;

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-lg px-3 py-2.5 mb-2`} data-testid={`deal-card-${deal.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${deal.health === 'at-risk' ? 'bg-red-400' : deal.health === 'won' ? 'bg-emerald-400' : deal.health === 'lost' ? 'bg-slate-400' : 'bg-emerald-400'}`} />
        <p className={`text-[11px] font-semibold flex-1 min-w-0 truncate ${tp}`}>{deal.name}</p>
        <span className={`text-xs font-bold ${deal.health === 'at-risk' ? (isDark ? 'text-red-400' : 'text-red-500') : tp}`}>${(deal.value / 1000).toFixed(0)}K</span>
      </div>
      <div className="flex items-center gap-2 ml-3.5">
        <User className={`w-3 h-3 ${ts}`} />
        <span className={`text-[9px] ${ts}`}>{deal.contact}</span>
      </div>
      <div className="flex items-center gap-2 ml-3.5 mt-1">
        <Clock className={`w-3 h-3 ${ts}`} />
        <span className={`text-[9px] ${ts}`}>{deal.nextStep}</span>
      </div>
      <div className="flex items-center gap-2 ml-3.5 mt-1.5">
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.04] text-white/40' : 'bg-black/[0.03] text-slate-500'}`}>{deal.probability}% prob</span>
        <span className={`text-[9px] ${ts}`}>Close: {deal.closeDate}</span>
        <span className={`text-[9px] ${ts}`}>{deal.owner}</span>
      </div>
      {deal.tags && (
        <div className="flex gap-1 mt-1.5 ml-3.5">
          {deal.tags.map(t => (
            <span key={t} className={`text-[8px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/10 text-indigo-400/70' : 'bg-indigo-50 text-indigo-500'}`}>{t}</span>
          ))}
        </div>
      )}
      {onDeepDive && (
        <div className="mt-2 ml-3.5">
          <button
            onClick={() => onDeepDive(deal.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
            data-testid={`deal-deepdive-${deal.id}`}
          >
            <Zap className="w-3 h-3" /> Deep Dive
          </button>
        </div>
      )}
    </div>
  );
};

const InsightCard = ({ item, isDark, onDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-black/[0.03]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`deal-insight-${item.id}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <div className="flex items-center justify-between mt-1">
          <p className={`text-[9px] ${ts}`}>Sally · {item.time}</p>
          {onDeepDive && (
            <button
              onClick={() => onDeepDive(item.id)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'} ${hoverBg}`}
              data-testid={`insight-deepdive-${item.id}`}
            >
              <Zap className="w-2.5 h-2.5" /> Deep Dive
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const V4SalesDeals = ({ isDark, onWorkflowToggle, workflowPanelOpen, activeUser, davidState, dealAdHocScenario, onDealAdHocDone }) => {
  const [stageFilter, setStageFilter] = useState(null);
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [deepDiveDealId, setDeepDiveDealId] = useState(null);
  const [deepDiveInsightId, setDeepDiveInsightId] = useState(null);
  const [newDealFlow, setNewDealFlow] = useState(false);
  const messagesEndRef = useRef(null);

  const isDavid = activeUser?.id === 'sales';

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const activeStages = ['Prospecting', 'Qualified', 'Proposal', 'Negotiation'];

  // Merge David's created deals into the main deal list
  const allDeals = useMemo(() => {
    const created = davidState?.createdDeals || [];
    return [...created, ...DEALS_DATA.deals];
  }, [davidState?.createdDeals]);

  const filteredDeals = allDeals.filter(d => {
    if (stageFilter) return d.stage === stageFilter;
    return activeStages.includes(d.stage);
  });

  const findDeal = (id) => allDeals.find(d => d.id === id);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || DEALS_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };

  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };

  const handleCreateDeal = () => {
    if (isDavid) { setNewDealFlow(true); return; }
    sendMessage('I want to create a new deal.', DEALS_CHAT_RESPONSES.create);
  };

  const handleBackFromNewDeal = () => {
    const scenario = DAVID_DEALS_CREATE['new-deal'];
    if (scenario?.createdDeal && davidState?.createDavidDeal) {
      davidState.createDavidDeal(scenario.createdDeal);
    }
    setNewDealFlow(false);
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Sally about deals..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="deals-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.sally} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Sally</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="deals-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Per-deal Deep Dive ── */
  if (deepDiveDealId) {
    const deal = findDeal(deepDiveDealId);
    const ddScenario = deal ? buildDealDeepDive(deal) : null;
    if (ddScenario) {
      return (
        <V4AdHocChat
          isDark={isDark}
          scenarioId={ddScenario.id}
          scenarios={{ [ddScenario.id]: ddScenario }}
          backLabel="Back to Deals"
          onBack={() => setDeepDiveDealId(null)}
        />
      );
    }
  }

  /* ── Insight Deep Dive ── */
  if (deepDiveInsightId) {
    const ddScenario = DAVID_INSIGHT_DD[deepDiveInsightId];
    if (ddScenario) {
      return (
        <V4AdHocChat
          isDark={isDark}
          scenarioId={ddScenario.id}
          scenarios={{ [ddScenario.id]: ddScenario }}
          backLabel="Back to Deals"
          onBack={() => setDeepDiveInsightId(null)}
        />
      );
    }
  }

  /* ── New Deal flow (David-only) ── */
  if (newDealFlow && isDavid) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId="new-deal"
        scenarios={DAVID_DEALS_CREATE}
        backLabel="Back to Deals"
        onBack={handleBackFromNewDeal}
      />
    );
  }

  /* ── Keypress A (David-only) ── */
  if (dealAdHocScenario && isDavid) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={dealAdHocScenario}
        scenarios={DAVID_DEALS_KEYPRESS}
        backLabel="Back to Deals"
        onBack={() => onDealAdHocDone?.()}
        skipFirstUserDelay
      />
    );
  }

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="deals-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="deals-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Deals</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.sally} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  // Group deals by stage for Kanban
  const dealsByStage = {};
  activeStages.forEach(s => { dealsByStage[s] = filteredDeals.filter(d => d.stage === s); });
  if (stageFilter && !activeStages.includes(stageFilter)) {
    dealsByStage[stageFilter] = filteredDeals;
  }

  return (
    <div className="flex flex-col h-full" data-testid="deals-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Target className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Deals</p>
            <span className={`text-[10px] ${ts}`}>{filteredDeals.length} active</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              {[null, ...activeStages, 'Closed Won', 'Closed Lost'].map(s => (
                <button key={s || 'all'} onClick={() => setStageFilter(s)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-colors ${stageFilter === s ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20') : `${border} ${ts} ${hoverBg}`}`}
                  data-testid={`deals-filter-${(s || 'all').toLowerCase().replace(/\s+/g, '-')}`}
                >{s || 'All'}</button>
              ))}
            </div>
            <button onClick={handleCreateDeal} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="deals-create-btn">
              <Plus className="w-3.5 h-3.5" /> New Deal
            </button>
          </div>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      {/* Kanban board */}
      <div className="flex-1 min-h-0 overflow-x-auto">
        <div className="flex gap-3 p-4 h-full min-w-max">
          {Object.entries(dealsByStage).map(([stage, deals]) => {
            const sc = isDark ? STAGE_COLORS[stage] : LIGHT_STAGE_COLORS[stage];
            const total = deals.reduce((sum, d) => sum + d.value, 0);
            return (
              <div key={stage} className="w-[280px] flex-shrink-0 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${sc?.bg} ${sc?.text} ${sc?.border}`}>{stage}</span>
                    <span className={`text-[10px] ${ts}`}>{deals.length}</span>
                  </div>
                  <span className={`text-[10px] font-medium ${tp}`}>${(total / 1000).toFixed(0)}K</span>
                </div>
                <div className="flex-1 overflow-y-auto space-y-0">
                  {deals.map(d => <DealCard key={d.id} deal={d} isDark={isDark} onDeepDive={isDavid ? setDeepDiveDealId : null} />)}
                  {deals.length === 0 && <p className={`text-[10px] ${ts} text-center py-4`}>No deals</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sally's insights */}
      <div className={`border-t ${border} px-5 py-3 flex-shrink-0`}>
        <div className="flex items-center gap-2 mb-2">
          <img src={AGENT_THUMBS.sally} alt="" className="w-4 h-4 rounded-full object-cover" />
          <p className={`text-[11px] font-semibold ${tp}`}>Sally's Insights</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {DEALS_DATA.insights.slice(0, 2).map(item => <InsightCard key={item.id} item={item} isDark={isDark} onDeepDive={isDavid ? setDeepDiveInsightId : null} />)}
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SalesDeals;
